/* eslint-disable no-nested-ternary */
import { MoreHoriz } from '@mui/icons-material';
import {
  Avatar,
  Box, Divider, IconButton, Menu, MenuItem, Paper, Stack, Typography,
} from '@mui/material';
import {
  HearIcon, MessageIcon, PingIcon,
} from 'assets/icons';
import { ContentMediaView } from 'components';
import { INewsfeed } from 'pages/newsfeed/interface';
import React from 'react';
import { useDispatch } from 'react-redux';
import { dateTimeFormat, getDateToUserTimezone } from 'utils/formats';
import { formatDistanceToNow } from 'date-fns';
import { IHighlightMedia } from 'pages/articles/interface';
import { newsfeedAction } from '../../slice';

interface Props {
  newsfeed: INewsfeed;
}

const NewsfeedGridItem = ({ newsfeed }: Props) => {
  const dispatch = useDispatch();
  const [selectedId, setSelectedID] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const isPostTypeNotDefault = newsfeed.postType !== 'default';

  const highlightMedia : IHighlightMedia | null = React.useMemo(() => {
    let media = null;

    if (newsfeed.postType === 'article' && newsfeed?.article?.highlightMedia) {
      media = newsfeed?.article?.highlightMedia;
    }
    if (newsfeed.postType === 'session' && newsfeed?.session?.highlightMedia) {
      media = newsfeed?.session?.highlightMedia;
    }
    return media;
  }, [newsfeed]);

  const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedID(id);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const pinHandler = () => {
    if (selectedId) {
      dispatch(newsfeedAction.pinNewsfeedItem(selectedId));
    }
    setTimeout(() => {
      dispatch(newsfeedAction.getNewsfeeds({
        keyword: '',
        page: 1,
        limit: 6,
        sortBy: 'Default',
      }));
    }, 1000);
  };
  const removeHandler = () => {
    if (selectedId) {
      dispatch(newsfeedAction.deleteNewsfeedItem(selectedId));
    }
    setTimeout(() => {
      dispatch(newsfeedAction.getNewsfeeds({
        keyword: '',
        page: 1,
        limit: 6,
        sortBy: 'Default',
      }));
    }, 1000);
  };

  return (
    <div data-testid="newsfeed-grid-item" className="col-sm-6 col-lg-4 my-2 my-lg-3">
      <Paper
        sx={{
          p: { xs: 4, md: 4 },
          borderRadius: (t) => t.custom.borderRadius,
          boxShadow: (t) => t.custom.boxShadow,
        }}
        style={{
          height: '500px',
          overflowY: 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" mb={4}>
            {`${dateTimeFormat(newsfeed.createdAt)}`}
          </Typography>

          <Box>
            <Box justifyContent="flex-end" alignItems="flex-end">
              <IconButton aria-label="pinned-icon" onClick={(e) => handleClick(e, newsfeed._id)}>
                <PingIcon sx={{ color: newsfeed.isPinned ? 'primary.main' : 'text.secondary', mr: 1 }} />
              </IconButton>
              <IconButton aria-label="more-horiz-icon" onClick={(e) => handleClick(e, newsfeed._id)}>
                <MoreHoriz sx={{ color: 'primary.main' }} />
              </IconButton>
              {/* menu  */}

              <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >

                <MenuItem onClick={pinHandler}>
                  {!newsfeed.isPinned ? (
                    <Typography
                      variant="body2"
                      mb={{ xs: 2, md: 1 }}
                    >
                      Pin to profile
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      mb={{ xs: 2, md: 1 }}
                    >
                      Unpin from profile
                    </Typography>
                  )}
                </MenuItem>
                <MenuItem onClick={removeHandler}>
                  <Typography
                    variant="body2"
                    mb={{ xs: 2, md: 1 }}
                  >
                    Delete
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleClose}>
                  <Typography
                    variant="body2"
                    mb={{ xs: 2, md: 1 }}
                  >
                    Cancel
                  </Typography>
                </MenuItem>

              </Menu>
              {/* menu */}
            </Box>

          </Box>
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            maxHeight: '264px',
          }}
        >
          <Typography
            variant="body2"
            mb={4}
          >
            {`${newsfeed.caption}`}
          </Typography>
        </Box>

        { newsfeed.postType === 'session'
          ? (
            <Box
              component="text"
              height={188}
              width="100%"
              sx={{
                display: 'block',
                objectFit: 'contain',
                borderRadius: (t) => t.custom.borderRadius,
                mb: 3,
              }}
            >
              <Typography
                fontWeight="medium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '7',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {`${newsfeed.session?.description.replace(/<\/?[^>]+(>|$)/g, '')}`}
              </Typography>
            </Box>
          ) : '' }
        { isPostTypeNotDefault && highlightMedia?.fileCategory === 'image' ? (
          <Box
            aria-label="article-image-viewer"
            component="img"
            height={188}
            width="100%"
            src={highlightMedia?.contentUrl}
            sx={{
              display: 'block',
              objectFit: 'contain',
              borderRadius: (t) => t.custom.borderRadius,
              mb: 3,
            }}
          />
        ) : isPostTypeNotDefault && highlightMedia?.fileCategory === 'video' ? (
          <ContentMediaView
            mediaType={highlightMedia?.fileCategory}
            url={highlightMedia?.contentUrl}
            fileName={`${highlightMedia?.contentUrl?.split('/').pop()}`}
          />
        ) : '' }
        {newsfeed.postType === 'default' && newsfeed?.mediaUrl?.fileCategory === 'image' ? (
          <Box
            aria-label="default-image-viewer"
            component="img"
            height={188}
            width="100%"
            src={`${newsfeed?.mediaUrl?.contentUrl}`}
            sx={{
              display: 'block',
              objectFit: 'contain',
              borderRadius: (t) => t.custom.borderRadius,
              mb: 3,
            }}
          />
        ) : newsfeed.postType === 'default' && newsfeed?.mediaUrl?.fileCategory === 'video' ? (
          <ContentMediaView
            mediaType={newsfeed?.mediaUrl?.fileCategory}
            url={newsfeed?.mediaUrl?.contentUrl}
            fileName={`${newsfeed?.mediaUrl?.contentUrl?.split('/').pop()}`}
          />
        ) : ''}

        <Box display="flex" alignItems="center" gap={8} mb={4}>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                svg: { color: (t) => t.palette.primary.main },
              },
            }}
          >
            <HearIcon aria-label="hear-icon" sx={{ color: (t) => t.palette.text.secondary }} />
            <Typography fontWeight="medium" color="text.secondary">
              {`${newsfeed.likes.length}`}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                svg: { color: (t) => t.palette.primary.main },
              },
            }}
          >
            <MessageIcon sx={{ color: (t) => t.palette.text.secondary }} />
            <Typography fontWeight="medium" color="text.secondary">
              {`${newsfeed.comments.length}`}
            </Typography>
          </Box>
          {/* <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              svg: { color: (t) => t.palette.primary.main },
            },
          }}
        >
          <SendIcon sx={{ color: (t) => t.palette.text.secondary }} />
        </Box> */}
        </Box>
        <Divider sx={{ mb: 4 }} />
        {newsfeed.comments.length === 0 ? <Typography variant="h6">No comments</Typography>
          : (
            <Stack spacing={3} sx={{ mb: 4 }}>
              <Typography variant="h6">Top comment</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  data-testid="avatar"
                  sx={{
                    height: '40',
                    width: '40',
                  }}
                  src={newsfeed.comments.slice(-1)[0].profileImage}
                />
                <Box>
                  <Typography fontWeight="medium" variant="body2">{newsfeed.comments.slice(-1)[0].name}</Typography>
                  <Typography fontWeight="medium" color="text.secondary" variant="body2">

                    {newsfeed.comments.slice(-1)[0].createdAt
                        && formatDistanceToNow(
                          getDateToUserTimezone(
                            newsfeed.comments.slice(-1)[0].createdAt,
                          ) as Date,
                          {
                            addSuffix: true,
                            includeSeconds: true,
                          },
                        )}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  p: 2,
                  borderRadius: (t) => t.custom.borderRadius,
                  background: (t) => t.palette.background.default,
                }}
              >
                {newsfeed.comments.slice(-1)[0].comment}
              </Typography>
            </Stack>
          )}
      </Paper>
    </div>

  );
};

export default NewsfeedGridItem;
